/* global location */
import I18n from 'lib/i18n'
import './styles.scss'

import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import Autosuggest from 'react-autosuggest'
import { kea } from 'kea'
import PropTypes from 'prop-types'
import { put } from 'redux-saga/effects'

import { indexSearchPath, indexSlugPath, teacherRequestSlug } from 'lib/utils/links'
import HighlightText from 'lib/utils/highlight-text'
import Spinner from 'lib/tags/spinner'
import Close from 'lib/icons/close'

import delay from 'lib/utils/delay'
import script from 'lib/utils/script'
import { GOOGLE_MAP_URL } from '~/scenes/settings'
import autoComplete from 'lib/utils/promise-autocomplete'
import scrollToView from 'lib/utils/scroll-to-view'

import headerProps from 'lib/selectors/header-props'

import searchSvg from './assets/search.svg'
import controller from './controller.rb'

const getSuggestionValue = suggestion => {
  const { is_tag: isTag } = suggestion

  if (suggestion.withText) {
    return suggestion.text
  } else {
    return isTag ? suggestion.sub_category_title : suggestion.name
  }
}
const alwaysTrue = () => true

function getSectionSuggestions (section) {
  return section ? section.suggestions : []
}

const isHomepage = id => id === 'homepage'
const shouldClear = (defaultValue, initialValue) => !defaultValue && !initialValue

export const logic = kea({
  propTypes: {
    onSearch: PropTypes.func, // called when searching
    performSearch: PropTypes.func, // if present, called instead of searching
    performLocate: PropTypes.func, // geolocate callback
    categorySuggestions: PropTypes.array,
    withMagnifyingGlass: PropTypes.bool,
    withButton: PropTypes.bool,
    withoutForm: PropTypes.bool,
    clearOnFocus: PropTypes.bool,
    defaultValue: PropTypes.string,
    id: PropTypes.string.isRequired,
    searchCities: PropTypes.bool,
    className: PropTypes.string,
    withMagnifierIcon: PropTypes.bool,
    isClearable: PropTypes.bool,
    label: PropTypes.string
  },

  key: props => props.id,
  path: key => ['scenes', 'shared', 'searchbox', key],

  actions: () => ({
    setValue: value => ({ value }),
    getSuggestions: value => ({ value }),
    setSuggestions: ({ value, suggestions }) => ({ value, suggestions }),
    setLoading: true,
    resetLoading: true
  }),

  defaults:
    ({ props }) =>
    state => {
      if (isHomepage(props.id)) {
        return { value: '' }
      }
      if (headerProps(state).isSearchPage && props.id === 'header-searchbox') {
        return { value: props.initialValue || '' }
      }
      return ''
    },

  reducers: ({ actions }) => ({
    value: [
      '',
      PropTypes.string,
      {
        [actions.setValue]: (_, payload) => payload.value
      }
    ],
    fetchValue: [
      '',
      PropTypes.string,
      {
        [actions.getSuggestions]: (state, payload) => (payload.value === '' ? '' : state),
        [actions.setSuggestions]: (_, payload) => payload.value
      }
    ],
    rawSuggestions: [
      [],
      PropTypes.array,
      {
        [actions.setSuggestions]: (_, payload) => payload.suggestions
      }
    ],
    isLoading: [
      false,
      PropTypes.bool,
      {
        [actions.setLoading]: () => true,
        [actions.resetLoading]: () => false
      }
    ],
    manualUpdate: [
      false,
      PropTypes.bool,
      {
        [actions.setValue]: (state, payload) => state || payload.value !== ''
      }
    ]
  }),
  selectors: ({ selectors }) => ({
    noResults: [
      () => [selectors.fetchValue, (_, props) => props.categorySuggestions, selectors.rawSuggestions],
      (fetchValue, defaultSuggestions, rawSuggestions) => {
        const suggestions = fetchValue ? rawSuggestions : defaultSuggestions
        return !!(suggestions && suggestions.length === 0)
      },
      PropTypes.bool
    ],
    suggestions: [
      () => [
        selectors.value,
        selectors.fetchValue,
        selectors.rawSuggestions,
        (_, props) => props.categorySuggestions,
        (_, props) => props.searchCities
      ],
      (value, fetchValue, rawSuggestions, defaultSuggestions, searchCities) => {
        let suggestions = [].concat((fetchValue ? rawSuggestions : defaultSuggestions) || [])

        if (fetchValue && suggestions.length === 0) {
          suggestions = [
            {
              name: searchCities
                ? I18n.t('react.search.landing.category.no_city_found', { text: fetchValue })
                : I18n.t('react.homepage.photo.search_missing', { text: fetchValue }),
              text: fetchValue,
              withText: true,
              slug: null,
              blank: true
            }
          ]
        }

        if (searchCities) {
          suggestions = suggestions.concat([
            {
              name: I18n.t('react.search.landing.geolocate'),
              text: value,
              withText: true,
              locate: true
            }
          ])
        }

        suggestions = suggestions.concat({
          name: I18n.t('react.homepage.photo.search_help'),
          text: value,
          withText: true,
          slug: teacherRequestSlug(I18n.locale),
          extra: true
        })

        return suggestions
      },
      PropTypes.array
    ],
    sections: [
      () => [selectors.suggestions, (_, props) => props.searchCities],
      (suggestions, searchCities) => [
        {
          suggestions
        }
      ],
      PropTypes.array
    ]
  }),
  takeLatest: ({ actions }) => ({
    * [actions.getSuggestions] (action) {
      const { searchCities } = this.props
      const { setSuggestions } = this.actions
      const { value } = action.payload

      if (searchCities) {
        yield delay(100)
        if (!window.google || !window.google.maps) {
          yield script(GOOGLE_MAP_URL + `&language=${I18n.locale}&callback=initMap`)
        }
        if (window.google && window.google.maps) {
          if (value) {
            const suggestions = yield autoComplete(value)
            yield put(setSuggestions({ suggestions, value }))
          } else {
            yield put(setSuggestions({ suggestions: [], value }))
          }
        }
      } else {
        yield delay(50)
        const { suggestions } = yield controller.getSuggestions({ term: value })
        yield put(setSuggestions({ suggestions, value }))
      }
    }
  })
})

class Searcbox extends Component {
  onChange = (event, { method, newValue }) => {
    const { setValue } = this.actions
    const { updateValue } = this.props
    setValue(newValue)
    updateValue && updateValue(newValue)
  }

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  onSuggestionsFetchRequested = ({ value, reason }) => {
    const { clearOnFocus, isClearable, updateSearchUi } = this.props
    const { getSuggestions } = this.actions

    if (['input-changed', 'input-focused'].indexOf(reason) > -1 && updateSearchUi) {
      updateSearchUi(true)
    }
    getSuggestions(reason === 'input-focused' && (clearOnFocus || isClearable) ? '' : value)

    // look at clearOnFocus() for a description on what happens here
  }

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested = () => {
    const { setSuggestions } = this.actions

    if (this._skipNextClear) {
      this._skipNextClear = true
      return
    }

    setSuggestions({ suggestions: [], value: '' })
  }

  clearOnFocus = () => {
    const { setValue } = this.actions
    setValue('')
    // after this react-autosuggest will call "onSuggestionsFetchRequested" with value == before clearing && reason == 'input-focused'
  }

  clearValue = () => {
    const { setValue } = this.actions
    const { updateValue } = this.props
    setValue('')
    const inputElement = window.document.getElementById('header-searchbox')
    if (inputElement) {
      inputElement.focus()
      updateValue && updateValue('')
    }
  }

  onBlur = () => {
    const { manualUpdate, defaultValue, clearOnFocus, updateSearchUi } = this.props
    const { setValue } = this.actions

    if (!clearOnFocus) {
      this._skipNextClear = true
    }

    if (updateSearchUi) {
      updateSearchUi(false)
    }

    if (manualUpdate && defaultValue) {
      setValue(defaultValue)
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps (nextProps) {
    const { setValue } = this.actions
    if (
      this.props.manualUpdate &&
      this.props.defaultValue !== nextProps.defaultValue &&
      typeof nextProps.defaultValue !== 'undefined'
    ) {
      this.actions.setValue(nextProps.defaultValue)
    }
    if (this.props.initialValue !== nextProps.initialValue) {
      setValue(nextProps.initialValue)
    }
  }

  onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
    const { performSearch, performLocate, searchCities } = this.props
    const { setLoading, setValue } = this.actions

    if (searchCities) {
      if (suggestion.locate) {
        setValue('')
        performLocate()
      } else if (suggestion.extra) {
        const url = location.origin + indexSlugPath(suggestion.slug)
        try {
          const win = window.open(url, '_blank')
          win.focus()
        } catch (e) {
          location.href = url
        }
      } else if (performSearch) {
        performSearch(suggestionValue, suggestion)
      }
    } else {
      const path = suggestion && suggestion.slug ? indexSlugPath(suggestion.slug) : indexSearchPath(suggestionValue)
      const url = location.origin + path

      if (suggestion.extra) {
        try {
          const win = window.open(url, '_blank')
          win.focus()
        } catch (e) {
          location.href = url
        }
      } else if (performSearch) {
        performSearch(suggestionValue, suggestion)
        this.refs.autoSuggest.input.blur()
      } else {
        location.href = url

        setLoading()
        this.props.onSearch && this.props.onSearch()
      }
    }
  }

  submit = event => {
    event.preventDefault()

    const { value, performSearch, updateSearchUi } = this.props
    const { setLoading } = this.actions

    if (updateSearchUi) {
      updateSearchUi(false)
    }

    console.log(`submitting: ${value}`)

    if (performSearch) {
      performSearch(value)
    } else {
      location.href = location.origin + indexSearchPath(value)

      setLoading()
      this.props.onSearch && this.props.onSearch()
      this.refs.autoSuggest.input.blur()
    }
  }

  onKeyUp = event => {
    if (event.keyCode === 13) {
      event.target.blur()

      if (this.props.withoutForm) {
        this.submit(event)
      }
    }
  }

  onFocus = () => {
    const { clearOnFocus } = this.props

    if (clearOnFocus) {
      this.clearOnFocus()
    }

    this.scrollBoxToViewIfFocused()
  }

  scrollBoxToViewIfFocused = () => {
    if (this.refs.autoSuggest) {
      const element = ReactDOM.findDOMNode(this.refs.autoSuggest)

      if (window.document.activeElement === this.refs.autoSuggest.input) {
        const width = window.innerWidth

        if (width < 960) {
          scrollToView({ element, adjustment: -15, force: true })
        }
      }
    }
  }

  componentDidMount () {
    const { autoFocus, isLoading, defaultValue, initialValue, id } = this.props
    const { resetLoading, setValue } = this.actions

    if (isLoading) {
      resetLoading()
    }

    window.addEventListener('resize', this.scrollBoxToViewIfFocused)
    window.addEventListener('pageshow', this.handlePageShow)

    if (isHomepage(id)) {
      window.addEventListener('pagehide', this.handlePageHide)
    }

    const input = this.refs.autoSuggest.input
    if (input && autoFocus) {
      input.focus()
    }

    if (isHomepage(id) && input) {
      if (shouldClear(defaultValue, initialValue) && input.value !== '') {
        window.requestAnimationFrame(() => {
          if (input) {
            input.value = ''
            setValue('')
          }
        })
      } else if (input.value !== this.props.value) {
        setValue(input.value)
      }
    }
  }

  componentWillUnmount () {
    const { id } = this.props
    window.removeEventListener('resize', this.scrollBoxToViewIfFocused)
    window.removeEventListener('pageshow', this.handlePageShow)

    if (isHomepage(id)) {
      window.removeEventListener('pagehide', this.handlePageHide)
    }
  }

  handlePageHide = () => {
    const { resetLoading, setValue } = this.actions
    const { id } = this.props

    if (isHomepage(id)) {
      resetLoading()
      setValue('')
    }
  }

  handlePageShow = event => {
    const { setValue, resetLoading } = this.actions
    const { defaultValue, initialValue, id } = this.props

    if (event.persisted && isHomepage(id)) {
      resetLoading()

      const input = this.refs.autoSuggest && this.refs.autoSuggest.input
      if (input) {
        if (shouldClear(defaultValue, initialValue)) {
          input.value = ''
          setValue('')
        } else {
          setValue(input.value)
        }
      }
    }
  }

  renderSectionTitle = () => {
    return <div>{I18n.t('react.homepage.photo.search_popular')}</div>
  }

  handleSearchClick = event => {
    event.preventDefault()
    const { value, performSearch } = this.props
    const { setLoading } = this.actions

    if (value) {
      if (performSearch) {
        performSearch(value)
      } else {
        setLoading()
        location.href = location.origin + indexSearchPath(value)
      }
    } else if (this.refs.autoSuggest) {
      const input = this.refs.autoSuggest.input
      if (input) {
        input.focus()
      }
    }
  }

  maginfyingGlass () {
    return <img src={searchSvg} alt={I18n.t('react.homepage.photo.search')} />
  }

  renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const { fetchValue } = this.props
    return (
      <div
        className={`item${isHighlighted ? ' focused' : ''}${suggestion.extra ? ' extra' : ''}${
          suggestion.locate ? ' locate' : ''
        }`}
      >
        <HighlightText highlight={fetchValue}>{suggestion.name}</HighlightText>
      </div>
    )
  }

  render () {
    const {
      value,
      fetchValue,
      suggestions,
      sections,
      withMagnifyingGlass,
      withButton,
      withoutForm,
      isLoading,
      searchCities,
      id,
      withMagnifierIcon,
      dataAttr,
      isClearable,
      label
    } = this.props

    // Autosuggest will pass through all these props to the input.
    const inputProps = {
      placeholder: searchCities
        ? I18n.t('react.search.landing.category.find_city')
        : I18n.t('react.homepage.photo.search_placeholder'),
      value,
      onChange: this.onChange,
      id: id || (searchCities ? 'location-search' : 'category-search'),
      name: 'search',
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onKeyUp: this.onKeyUp,
      label: label,
      'aria-label': searchCities
        ? I18n.t('react.search.landing.category.find_city')
        : I18n.t('react.homepage.photo.search_placeholder')
    }

    const suggest = (
      <Autosuggest
        ref='autoSuggest'
        shouldRenderSuggestions={alwaysTrue}
        focusInputOnSuggestionClick={false}
        suggestions={fetchValue || searchCities ? suggestions : sections}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionSelected={this.onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={this.renderSuggestion}
        inputProps={inputProps}
        multiSection={!fetchValue && !searchCities}
        renderSectionTitle={this.renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
      />
    )
    // in case we want to highlight the first result, e.g. if moving the location search over to this:
    // highlightFirstSuggestion={!!fetchValue && fetchValue === value && !noResults}

    const className = `apprentus-searchbox${withMagnifyingGlass ? ' with-magnifying-glass' : ''}${
      searchCities ? ' search-cities' : ' search-categories'
    }${fetchValue ? ' with-value' : ''} ${this.props.className}`
    const buttonClassName = `apprentus-searchbox-button${withMagnifyingGlass ? ' magnifying-glass' : ''}`

    // Finally, render it!
    if (withoutForm) {
      return (
        <div className={className} data-attr={dataAttr}>
          {suggest}
          {isClearable && !!value.trim().length && (
            <Close inputMode style={{ margin: 0 }} onClick={this.clearValue} color='white' />
          )}
          {withMagnifierIcon ? (
            <div
              onClick={this.handleSearchClick}
              className={`svg-rotate-rtl magnifier-container${isLoading ? ' adjusted-magnifier' : ''}`}
            >
              {isLoading ? (
                <Spinner color='white' />
              ) : (
                <svg focusable={false} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
                  <path d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z' />
                </svg>
              )}
            </div>
          ) : withButton || withMagnifyingGlass ? (
            <button className={buttonClassName} type='submit' onClick={this.handleSearchClick}>
              {isLoading ? (
                <Spinner color='white' />
              ) : withMagnifyingGlass ? (
                this.maginfyingGlass()
              ) : (
                I18n.t('react.homepage.photo.search')
              )}
            </button>
          ) : null}
        </div>
      )
    } else {
      return (
        <form onSubmit={this.submit} action='/index_search' method='get' className={className} data-attr={dataAttr}>
          <div className='container-separator' />
          {suggest}
          {isClearable && !!value.trim().length && (
            <Close inputMode style={{ margin: 0 }} onClick={this.clearValue} color='white' />
          )}
          {withMagnifierIcon ? (
            <div
              onClick={this.handleSearchClick}
              className={`svg-rotate-rtl magnifier-container${isLoading ? ' adjusted-magnifier' : ''}`}
            >
              {isLoading ? (
                <Spinner color='white' />
              ) : (
                <svg focusable={false} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
                  <path d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z' />
                </svg>
              )}
            </div>
          ) : withButton || withMagnifyingGlass ? (
            <button className={buttonClassName} type='submit' onClick={this.handleSearchClick}>
              {isLoading ? (
                <Spinner color='white' />
              ) : withMagnifyingGlass ? (
                this.maginfyingGlass()
              ) : (
                I18n.t('react.homepage.photo.search')
              )}
            </button>
          ) : null}
        </form>
      )
    }
  }
}

export default logic(Searcbox)
